import React, { useState, useEffect } from 'react';
import { readSettingFile, writeSettingFile } from 'shared/Settings';
import { printTest, getAvailableComPorts } from 'utils/printer';

export default function SettingPrinter(props) {
  if (props && !props.onClose) {
    console.warn('Set onClose callback function.');
  }

  const settings = readSettingFile() || {};

  const [cutType, cutTypeSet] = useState(settings.cutType ?? 'GS_V_66');
  const [baudRate, baudRateSet] = useState(settings.baudRate ?? 9600);
  const [grantedPorts, grantedPortsSet] = useState([]);
  const [selectedPortIndex, setSelectedPortIndex] = useState(0);

  useEffect(() => {
    loadGrantedPorts();
  }, []);

  // 승인된 포트 목록 불러오기
  const loadGrantedPorts = async () => {
    const ports = await getAvailableComPorts();
    grantedPortsSet(ports);
    if (ports.length === 0) {
      setSelectedPortIndex(0);
    }
  };

  // 1. 새로운 프린터 연결 (권한 요청)
  const handleConnectPort = async () => {
    if (!('serial' in navigator)) {
      alert('현재 브라우저는 Web Serial API를 지원하지 않습니다. Chrome 또는 Edge를 사용해 주세요.');
      return;
    }

    try {
      await navigator.serial.requestPort();
      await loadGrantedPorts();
      alert('시리얼 포트가 연결되었습니다.');
    } catch (error) {
      if (error.name !== 'NotFoundError') {
        console.error('포트 요청 오류:', error);
      }
    }
  };

  // 2. 선택된 프린터 연결 해제 (포트 권한 제거)
  const handleForgetPort = async () => {
    const targetPort = grantedPorts[selectedPortIndex];

    if (!targetPort) {
      alert('해제할 포트가 선택되지 않았습니다.');
      return;
    }

    try {
      if (targetPort.forget) {
        await targetPort.forget();
        alert('선택한 프린터 연결 권한이 해제되었습니다.');
        await loadGrantedPorts();
      } else {
        alert('현재 브라우저 버전에서는 forget() 기능을 지원하지 않습니다.');
      }
    } catch (error) {
      console.error('포트 해제 오류:', error);
      alert('포트 해제 중 오류가 발생했습니다.');
    }
  };

  const saveSettings = () => {
    const newSettings = {
      ...settings,
      cutType,
      baudRate: parseInt(baudRate, 10),
      portIndex: selectedPortIndex,
    };
    writeSettingFile(newSettings);
    return newSettings;
  };

  const handleTestPrint = () => {
    console.log('테스트인쇄시작');
    saveSettings();
    console.log('selectedPortIndex',selectedPortIndex);
    const targetPort = grantedPorts[selectedPortIndex];
    console.log('targetPort',targetPort);
    if (!targetPort) {
      alert('연결된 프린터 포트가 없습니다.먼저 [🔌 연결] 버튼을 눌러 시리얼 포트를 선택해 주세요.');
      return;
    }    
    printTest(targetPort, baudRate);
  };

  return (
    <div className="flex flex-col bg-cust-gray items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none p-4 md:p-6">
      <div className="w-full max-w-lg md:max-w-xl">

        {/* 안내 문구 카드 */}
        <div className="bg-white p-3 md:p-4 rounded border border-cust-line mb-4 text-center">
          <p className="text-gray-700 font-bold text-base md:text-lg">🖨️ 프린터 출력 설정</p>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            프린터를 새롭게 연결하거나, 사용하지 않는 장치를 해제할 수 있습니다.
          </p>
        </div>

        <div className="border-cust-medium-gray border-b my-4" />

        {/* 설정 입력 영역 */}
        <div className="space-y-4">

          {/* 1. 프린터 포트 설정 */}
          <div className="flex flex-col sm:flex-row sm:items-center">
            <label className="block tracking-wide text-base md:text-lg font-bold w-full sm:w-32 mb-1 sm:mb-0 shrink-0">
              프린터 포트
            </label>
            
            {/* PC 기준: 셀렉트 박스와 버튼 그룹이 우측에 나란히 배치 */}
            <div className="flex flex-row items-center w-full sm:flex-1 gap-2 min-w-0">
              <div className="flex-1 min-w-0">
                {grantedPorts.length > 0 ? (
                  <select
                    className="border border-gray-300 p-2 rounded text-sm md:text-base w-full focus:outline-none focus:border-primary bg-white text-gray-900 truncate"
                    value={selectedPortIndex}
                    onChange={(e) => setSelectedPortIndex(Number(e.target.value))}
                  >
                    {grantedPorts.map((port, idx) => {
                      const info = port.getInfo ? port.getInfo() : {};
                      const label = info.usbVendorId
                        ? `포트 ${idx + 1} (VID:${info.usbVendorId})`
                        : `연결된 포트 ${idx + 1}`;
                      return (
                        <option key={idx} value={idx} className="bg-white text-gray-900">
                          {label}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <div className="border border-gray-200 bg-gray-50 p-2 rounded text-sm text-red-500 font-medium truncate">
                    연결된 포트 없음
                  </div>
                )}
              </div>

              {/* 포트 연결 및 해제 버튼 그룹 (우측 고정) */}
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={handleConnectPort}
                  className="bg-gray-700 text-white px-3 py-2 rounded text-xs md:text-sm hover:bg-gray-800 transition-all whitespace-nowrap"
                >
                  🔌 연결
                </button>

                {grantedPorts.length > 0 && (
                  <button
                    type="button"
                    onClick={handleForgetPort}
                    className="bg-red-600 text-white px-3 py-2 rounded text-xs md:text-sm hover:bg-red-700 transition-all whitespace-nowrap"
                  >
                    🗑️ 해제
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 2. 보드레이트 설정 (버튼이 없으므로 우측 공간을 100% 채움 -> 포트 박스보다 넓음) */}
          <div className="flex flex-col sm:flex-row sm:items-center">
            <label className="block tracking-wide text-base md:text-lg font-bold w-full sm:w-32 mb-1 sm:mb-0 shrink-0">
              보드레이트
            </label>
            <div className="w-full sm:flex-1">
              <select
                className="border border-gray-300 p-2 rounded text-sm md:text-base w-full focus:outline-none focus:border-primary bg-white text-gray-900"
                value={baudRate}
                onChange={(e) => baudRateSet(parseInt(e.target.value, 10))}
              >
                <option value={9600} className="bg-white text-gray-900">9600 (기본값)</option>
                <option value={19200} className="bg-white text-gray-900">19200</option>
                <option value={38400} className="bg-white text-gray-900">38400</option>
                <option value={115200} className="bg-white text-gray-900">115200</option>
              </select>
            </div>
          </div>

          <div className="border-cust-medium-gray border-b my-4" />

          {/* 3. 커팅 방식 설정 (버튼이 없으므로 우측 공간을 100% 채움 -> 포트 박스보다 넓음) */}
          <div className="flex flex-col sm:flex-row sm:items-center">
            <label className="block tracking-wide text-base md:text-lg font-bold w-full sm:w-32 mb-1 sm:mb-0 shrink-0">
              커팅 방식
            </label>
            <div className="w-full sm:flex-1">
              <select
                className="border border-gray-300 p-2 rounded text-sm md:text-base w-full focus:outline-none focus:border-primary bg-white text-gray-900 truncate"
                value={cutType}
                onChange={(e) => cutTypeSet(e.target.value)}
              >
                <option value="GS_V_66" className="bg-white text-gray-900">표준 타입 A (BIXOLON, SAM4S 등)</option>
                <option value="ESC_I" className="bg-white text-gray-900">표준 타입 B (SEWOO, Lukhan 등)</option>
                <option value="GS_V_0" className="bg-white text-gray-900">표준 타입 C (EPSON 등)</option>
                <option value="ESC_M" className="bg-white text-gray-900">기타 / 호환 타입</option>
              </select>
            </div>
          </div>

        </div>

        {/* 하단 버튼 영역 */}
        <div className="flex flex-row space-x-2 justify-between mt-6 md:mt-8">
          <button
            className="w-1/2 h-12 md:h-16 text-white bg-primary hover:bg-opacity-90 transition-all font-bold text-base md:text-lg rounded"
            type="button"
            onClick={handleTestPrint}
          >
            테스트 인쇄
          </button>

          <button
            className="w-1/2 h-12 md:h-16 text-white bg-primary hover:bg-opacity-90 transition-all font-bold text-base md:text-lg rounded"
            type="button"
            onClick={() => {
              saveSettings();
              if (props.onClose) props.onClose();
            }}
          >
            저장
          </button>
        </div>

      </div>
    </div>
  );
}